import {
  ChangeEventHandler,
  KeyboardEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { get } from '../../api';

export default function FindUser({ name, value, onChange, disabled }: any) {
  const [searchString, setSearchString] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  const [index, setIndex] = useState<number | null>(null);

  const resetSearchString = () => {
    const user = users.find((user) => user.id === value);
    setSearchString(user?.username ?? '');
  };

  // Re-syncs whenever the assigned value changes AND whenever the user list
  // arrives — on a fresh page load (e.g. the skirmish lobby prefilling
  // slot 1 with the current player) the /users fetch resolves after this
  // component mounts, so a value-only dep would leave the input showing an
  // empty box next to an already-prefilled userId (and a click-blur on that
  // empty box would then commit '' and wipe the prefill).
  useEffect(resetSearchString, [value, users]);

  useEffect(() => {
    get('users')
      .then(setUsers)
      .catch(() => {});
  }, []);

  const filteredUsers = useMemo(
    () => users.filter((user) => user.username.includes(searchString)),
    [users, searchString]
  );

  const handleKeyDown: KeyboardEventHandler = (event) => {
    if (event.key === 'ArrowDown') {
      if (index === null) {
        setIndex(0);
      } else {
        setIndex(Math.min(index + 1, filteredUsers.length - 1));
      }
    } else if (event.key === 'ArrowUp') {
      if (index === null) {
        setIndex(0);
      } else {
        setIndex(Math.max(index - 1, 0));
      }
    } else {
      setIndex(null);
    }

    if (event.key === 'Enter') {
      onChange?.({
        target: {
          name,
          value: filteredUsers[index ?? 0].id,
        },
      });
      setSearchString(filteredUsers[index ?? 0].username);
      setFocused(false);
    }
  };

  const handleFocus = () => {
    setIndex(null);
    setFocused(true);
  };

  const handleBlur = () => {
    resetSearchString();
    onChange?.({
      target: {
        name,
        value:
          searchString.length === 0 || filteredUsers.length === 0
            ? ''
            : filteredUsers[0].id,
      },
    });
    setFocused(false);
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setFocused(true);
    setSearchString(event.target.value);
  };

  return (
    <div className={`dropdown ${focused ? 'is-active' : ''}`}>
      <input
        ref={ref}
        className="input"
        type="text"
        value={searchString}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      <div className="dropdown-menu" id="dropdown-menu" role="menu">
        <div className="dropdown-content">
          {filteredUsers.length === 0 && (
            <div className="dropdown-item">--None--</div>
          )}
          {filteredUsers.map((user, i) => {
            const isActive = i === index;

            const handleMouseDown = () => {
              setSearchString(user.username);
              onChange?.({
                target: {
                  name,
                  value: user.id,
                },
              });
            };

            return (
              <a
                className={`dropdown-item ${isActive ? 'is-active' : ''}`}
                onMouseDown={handleMouseDown}
              >
                {user.username}
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}
